import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { InputErrorBoundary } from "@/components/common";
import { MobileChatInput, useChatStore } from "@/features/chat";
import { useChatDock } from "../hooks/use-chat-dock";
import { useChatDockStore } from "../store/chat-dock-store";

/**
 * Dock'un boş hâli: karşılama + composer. İlk mesajda konuşma oluşturulur
 * ve dock aynı yerde ChatDockConversation'a döner — sayfa değişmez.
 *
 * Mesaj local state'te; global chat-store'daki `message` landing composer'ına
 * ait, ikisi karışmasın. Ekler ise picker üzerinden global store'a düşer,
 * oradan okunur.
 */
export const ChatDockComposer: React.FC = () => {
  const { t } = useTranslation();
  const { module, startConversation, isCreating } = useChatDock();
  const composerRestore = useChatDockStore((s) => s.composerRestore);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!composerRestore) return;
    setMessage(composerRestore.message);
    if (composerRestore.attachments?.length) {
      useChatStore.getState().setSelectedAttachments(composerRestore.attachments);
    }
    useChatDockStore.getState().setComposerRestore(null);
  }, [composerRestore]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { selectedAttachments, clearSelectedAttachments } = useChatStore();

  if (!module) return null;
  const moduleName = t(module.nameKey);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting || isCreating) return;
    const trimmed = message.trim();
    if (!trimmed && selectedAttachments.length === 0) return;

    const toSend =
      trimmed ||
      (selectedAttachments.length > 0 ? t("chat.fileAttachedMessage") : "");
    const attachments =
      selectedAttachments.length > 0 ? selectedAttachments : undefined;

    setIsSubmitting(true);
    try {
      const ok = await startConversation(toSend, attachments);
      if (ok) {
        setMessage("");
        clearSelectedAttachments();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      {/* Karşılama — composer'ın üstünde, ortalanmış */}
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 px-6 pb-36 text-center">
        <span className="flex size-12 items-center justify-center rounded-2xl bg-module-accent-soft text-module-accent">
          <module.icon size={24} />
        </span>
        <p className="text-base font-semibold text-text-primary dark:text-gray-100">
          {t("chatDock.greeting", { module: moduleName })}
        </p>
        <p className="max-w-[28ch] text-xs text-text-tertiary dark:text-gray-400">
          {t("chatDock.hint")}
        </p>
      </div>

      {/* MobileChatInput absolute bottom-0 — bu kapsayıcıya oturur */}
      <InputErrorBoundary>
        <MobileChatInput
          customMessage={message}
          customSetMessage={setMessage}
          customHandleSubmit={handleSubmit}
          customIsLoading={false}
          customIsSubmitting={isSubmitting || isCreating}
        />
      </InputErrorBoundary>
    </div>
  );
};
